import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import './EventsPage.css';
import Footer from '../components/Footer';

function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [newEvent, setNewEvent] = useState({
    name: '',
    datetime: '',
    description: '',
    images: [],
    location: '',
    tickets: [{ type: '', price: '', qty: '' }],
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [editingImageUploading, setEditingImageUploading] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'events'));
        const eventList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(eventList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching events:', error);
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleDeleteEvent = async (eventId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this event?'
    );
    if (!confirmDelete) return;

    try {
      await updateDoc(doc(db, 'events', eventId), { archived: true });
      setEvents((prev) => prev.filter((event) => event.id !== eventId));
      alert('Event removed successfully!');
    } catch (error) {
      console.error('Error archiving event:', error);
      alert('Failed to remove event.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent((prev) => ({ ...prev, [name]: value }));
  };

  const handleTicketChange = (index, field, value) => {
    const updatedTickets = newEvent.tickets.map((ticket, i) =>
      i === index ? { ...ticket, [field]: value } : ticket
    );
    setNewEvent((prev) => ({ ...prev, tickets: updatedTickets }));
  };

  const addTicket = () => {
    setNewEvent((prev) => ({
      ...prev,
      tickets: [...prev.tickets, { type: '', price: '', qty: '' }],
    }));
  };

  const removeTicket = (index) => {
    const updatedTickets = newEvent.tickets.filter((_, i) => i !== index);
    setNewEvent((prev) => ({ ...prev, tickets: updatedTickets }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      const storage = getStorage();
      const urls = await Promise.all(
        files.map(async (file) => {
          const storageRef = ref(
            storage,
            `event_images/${Date.now()}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        })
      );
      setNewEvent((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setNewEvent((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const openEditModal = (event) => {
    setEventToEdit(event);
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEventToEdit((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditTicketChange = (index, field, value) => {
    const updatedTickets = [...eventToEdit.tickets];
    updatedTickets[index][field] = value;
    setEventToEdit((prev) => ({ ...prev, tickets: updatedTickets }));
  };

  const addEditTicket = () => {
    setEventToEdit((prev) => ({
      ...prev,
      tickets: [...prev.tickets, { type: '', price: '', qty: '' }],
    }));
  };

  const removeEditTicket = (index) => {
    const updatedTickets = eventToEdit.tickets.filter((_, i) => i !== index);
    setEventToEdit((prev) => ({ ...prev, tickets: updatedTickets }));
  };

  const handleImageUploadForEdit = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !eventToEdit) return;

    setEditingImageUploading(true);
    try {
      const storage = getStorage();
      const urls = await Promise.all(
        files.map(async (file) => {
          const storageRef = ref(
            storage,
            `event_images/${Date.now()}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        })
      );
      setEventToEdit((prev) => ({
        ...prev,
        images: [...prev.images, ...urls],
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setEditingImageUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!eventToEdit) return;

    const ticketTypes = eventToEdit.tickets.map((t) => t.type.trim().toLowerCase());
    const hasDuplicates = ticketTypes.some((type, i) => ticketTypes.indexOf(type) !== i);
    if (hasDuplicates) {
      alert('Each ticket type must have a unique name. Please remove duplicate ticket types.');
      return;
    }

    try {
      const eventRef = doc(db, 'events', eventToEdit.id);
      await updateDoc(eventRef, {
        name: eventToEdit.name,
        datetime: eventToEdit.datetime,
        location: eventToEdit.location,
        description: eventToEdit.description,
        images: eventToEdit.images,
        tickets:
          eventToEdit.tickets.length > 0
            ? eventToEdit.tickets
            : [{ type: 'Sales Start Soon', price: 0 }],
      });

      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventToEdit.id ? eventToEdit : event
        )
      );

      alert('Event updated successfully!');
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newEvent.images.length === 0) {
      alert('Please upload at least one image before submitting.');
      return;
    }

    const ticketTypes = newEvent.tickets.map((t) => t.type.trim().toLowerCase());
    const hasDuplicates = ticketTypes.some((type, i) => ticketTypes.indexOf(type) !== i);
    if (hasDuplicates) {
      alert('Each ticket type must have a unique name. Please remove duplicate ticket types.');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'events'), {
  ...newEvent,
  tickets: newEvent.tickets.map(t => ({ ...t, sold: 0 })),
  createdAt: new Date(),
});
      setEvents((prevEvents) => [
        ...prevEvents,
        { id: docRef.id, ...newEvent },
      ]);
      alert('Event added successfully!');
      setNewEvent({
        name: '',
        datetime: '',
        description: '',
        images: [],
        location: '',
        tickets: [{ type: '', price: '', qty: '' }],
      });
      setShowModal(false);
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event.');
    }
  };

  if (loading) {
    return <div className="loading">Loading events...</div>;
  }

  return (
    <div className="events-container">
      <h1 className="events-heading">Events List</h1>

      <div className="events-table-container">
        <table className="events-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Event Name</th>
              <th>Date & Time</th>
              <th>Location</th>
              <th>Description</th>
              <th>Tickets</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td>
                  {event.images?.[0] ? (
                    <img
                      src={event.images[0]}
                      alt={event.name}
                      className="event-image"
                    />
                  ) : (
                    'No Image Available'
                  )}
                </td>
                <td>{event.name}</td>
                <td>{event.datetime}</td>
                <td>{event.location}</td>
                <td className="event-description" dangerouslySetInnerHTML={{ __html: event.description.replace(/&nbsp;/g, ' ') }} />
                <td>
                  {event.tickets && event.tickets.length > 0 ? (
                    <table className="inner-table">
  <thead>
    <tr>
      <th>Type</th>
      <th>Price ($)</th>
      <th>Qty Left</th>
    </tr>
  </thead>
  <tbody>
    {event.tickets.map((ticket, index) => (
      <tr key={index}>
        <td>{ticket.type || 'N/A'}</td>
        <td>${parseFloat(ticket.price || 0).toFixed(2)}</td>
        <td>
          {!ticket.qty
            ? 'Unlimited'
            : (ticket.sold ?? 0) >= parseInt(ticket.qty)
            ? '🚫 Sold Out'
            : `${parseInt(ticket.qty) - (ticket.sold ?? 0)} left`}
        </td>
      </tr>
    ))}
  </tbody>
</table>
                  ) : (
                    'No tickets available'
                  )}
                </td>
                <td>
                  <button
                    className="edit-event-button"
                    onClick={() => openEditModal(event)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="delete-event-button"
                    onClick={() => handleDeleteEvent(event.id, event.images)}
                  >
                    ❌ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="add-event-button" onClick={() => setShowModal(true)}>
        Add Event
      </button>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New Event</h2>
            <form onSubmit={handleSubmit} className="event-form">
              <input
                type="text"
                name="name"
                placeholder="Event Name"
                value={newEvent.name}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="datetime"
                placeholder="Date & Time"
                value={newEvent.datetime}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="location"
                placeholder="Location"
                value={newEvent.location}
                onChange={handleInputChange}
                required
              />

              <h3>Event Images</h3>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                multiple
                required
              />
              {uploading && <p>Uploading images...</p>}

              <div className="uploaded-images-preview">
                {newEvent.images.map((img, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={img} alt={`Preview ${index}`} />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="remove-image-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <ReactQuill
                theme="snow"
                value={newEvent.description}
                onChange={(value) => setNewEvent((prev) => ({ ...prev, description: value }))}
                style={{ background: 'white', borderRadius: '5px', marginBottom: '10px' }}
              />

              <h3>Tickets</h3>
              {newEvent.tickets.map((ticket, index) => (
                <div key={index} className="ticket-inputs">
                  <input
                    type="text"
                    placeholder="Type (e.g., Adult, Child)"
                    value={ticket.type}
                    onChange={(e) =>
                      handleTicketChange(index, 'type', e.target.value)
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={ticket.price}
                    onChange={(e) =>
                      handleTicketChange(index, 'price', e.target.value)
                    }
                    required
                  />
                  <input
  type="number"
  placeholder="Qty Available (blank = unlimited)"
  value={ticket.qty}
  onChange={(e) =>
    handleTicketChange(index, 'qty', e.target.value)
  }
  min="0"
/>
                  {newEvent.tickets.length > 1 && (
                    <button type="button" onClick={() => removeTicket(index)}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-ticket-button"
                onClick={addTicket}
              >
                ➕ Add Ticket Type
              </button>

              <div className="modal-buttons">
                <button type="submit">Add Event</button>
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModalOpen && eventToEdit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Event</h2>
            <form className="event-form">
              <input
                type="text"
                name="name"
                value={eventToEdit.name}
                onChange={handleEditInputChange}
                required
              />
              <input
                type="text"
                name="datetime"
                value={eventToEdit.datetime}
                onChange={handleEditInputChange}
                required
              />
              <input
                type="text"
                name="location"
                value={eventToEdit.location}
                onChange={handleEditInputChange}
                required
              />
              <ReactQuill
                theme="snow"
                value={eventToEdit.description}
                onChange={(value) => setEventToEdit((prev) => ({ ...prev, description: value }))}
                style={{ background: 'white', borderRadius: '5px', marginBottom: '10px' }}
              />

              <h3>Event Images</h3>
              <div className="uploaded-images-preview">
                {eventToEdit.images?.map((img, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={img} alt={`Preview ${index}`} />
                    <button
                      type="button"
                      onClick={() => {
                        setEventToEdit((prev) => ({
                          ...prev,
                          images: prev.images.filter((_, i) => i !== index),
                        }));
                      }}
                      className="remove-image-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={handleImageUploadForEdit}
                multiple
              />
              {editingImageUploading && <p>Uploading images...</p>}

              <h3>Tickets</h3>
              {eventToEdit.tickets.map((ticket, index) => (
                <div key={index} className="ticket-inputs">
                  <input
                    type="text"
                    placeholder="Type (e.g., Adult, Child)"
                    value={ticket.type}
                    onChange={(e) =>
                      handleEditTicketChange(index, 'type', e.target.value)
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={ticket.price}
                    onChange={(e) =>
                      handleEditTicketChange(index, 'price', e.target.value)
                    }
                    required
                  />
                  <input
  type="number"
  placeholder="Qty Available (blank = unlimited)"
  value={ticket.qty ?? ''}
  onChange={(e) =>
    handleEditTicketChange(index, 'qty', e.target.value)
  }
  min="0"
/>
                  {eventToEdit.tickets.length > 1 && (
                    <button
                      type="button"
                      className="remove-ticket-button"
                      onClick={() => removeEditTicket(index)}
                    >
                      ❌ Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-ticket-button"
                onClick={addEditTicket}
              >
                ➕ Add Ticket Type
              </button>

              <div className="modal-buttons">
                <button type="button" onClick={handleSaveEdit}>
                  💾 Save Changes
                </button>
                <button type="button" onClick={() => setEditModalOpen(false)}>
                  ❌ Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default EventsPage;
